import React, {useMemo, useState} from 'react'
import {Spin} from 'antd'
import queryString from 'query-string'
import {TABS, UNASSIGNED_OBJ} from '../constants'
import {addDays, fmAsOf, startOfMonth, today} from '../helpers/format'
import useNgRateTab from '../hooks/useNgRateTab'
import {TooltipProvider, tipHandlers, useTooltip} from './charts/Tooltip'
import FilterBar from './FilterBar'
import TAB_COMPONENTS from '../tabs'
import '../styles/index.scss'

const _presetRange = (preset, maxDate) => {
    if (preset === '30') return {from: addDays(maxDate, -29), to: maxDate}
    if (preset === '90') return {from: addDays(maxDate, -89), to: maxDate}

    return {from: startOfMonth(maxDate), to: maxDate}
}

// "Chưa gán" gửi dims rỗng (backend lọc dim_id = ''); không chọn gì thì không gửi dims
const _dimsParam = (obj) => {
    if (obj === UNASSIGNED_OBJ) return ''

    return obj || undefined
}

const _tabParams = (tab, filters, maxDate) => {
    if (tab.key === 'report') return {from: `${maxDate.slice(0, 4)}-01-01`, to: maxDate}

    const groups = filters.obj && tab.key === 'process' ? filters.obj : (filters.stage || undefined)

    return {
        from: filters.from,
        to: filters.to,
        groups: tab.key === 'process' ? groups : undefined,
        dims: tab.key !== 'process' ? _dimsParam(filters.obj) : undefined,
    }
}

// useTooltip() cần đọc TooltipProvider dựng ở NgRatePage bên dưới, nên phải là component con riêng (không gọi hook
// ngay trong NgRatePage — lúc đó còn ở ngoài Provider)
const TabNav = ({activeKey, onSelect}) => {
    const tooltip = useTooltip()

    return (
        <div className="tabs" role="tablist">
            {TABS.map(item => (
                <button
                    key={item.key} type="button" className="tab" role="tab" aria-selected={item.key === activeKey}
                    onClick={() => onSelect(item.key)} {...tipHandlers(tooltip, <div>{item.hint}</div>)}
                >
                    {item.label}
                </button>
            ))}
        </div>
    )
}

// Tab con nằm trong tab "Thống kê NG rate" của dashboard; tab con giữ trong state vì dashboard tự quản URL
const NgRatePage = () => {
    const [mock] = useState(() => queryString.parse(window.location.search).mock === '1')
    const [tabKey, setTabKey] = useState(TABS[0].key)
    const tab = TABS.find(item => item.key === tabKey)
    const maxDate = today()

    const [filters, setFilters] = useState(() => ({preset: 'm', stage: '', obj: '', ..._presetRange('m', maxDate)}))

    const params = useMemo(() => ({..._tabParams(tab, filters, maxDate), ...(mock ? {mock: 1} : {})}), [tab, filters, maxDate, mock])
    const {data, loading, error} = useNgRateTab(tab.key, params)
    const current = data && data.tab === tab.key ? data : null
    const TabComponent = TAB_COMPONENTS[tab.key]
    const asOf = data ? fmAsOf(data.as_of) : null

    const _selectTab = (key) => {
        setFilters(previous => ({...previous, stage: '', obj: ''}))
        setTabKey(key)
    }

    const _changeFilters = (patch) => setFilters(previous => {
        const next = {...previous, ...patch}
        if (next.from > next.to) {
            if (patch.from) next.to = next.from
            else next.from = next.to
        }

        return next
    })

    return (
        <TooltipProvider>
            <div className="NgRatePage">
                <header className="ng-header">
                    <div className="hd">
                        <div>
                            <h1 className="sub">Báo cáo chất lượng sản xuất — lọc theo khoảng thời gian, công đoạn và đối tượng</h1>
                        </div>
                        <div className="spacer"/>
                        <span className="tagline">{asOf ? `Số liệu tính đến ${asOf}` : 'Chưa có lần chạy số liệu'}</span>
                        {mock && <span className="tagline warn">Dữ liệu mẫu</span>}
                    </div>
                </header>

                <nav className="ng-nav">
                    <TabNav activeKey={tab.key} onSelect={_selectTab}/>
                </nav>

                <div className="wrap">
                    {tab.key !== 'report' && (
                        <FilterBar
                            tab={tab} filters={filters} maxDate={maxDate} data={current}
                            onPreset={(preset) => _changeFilters(preset === 'cus' ? {preset} : {preset, ..._presetRange(preset, maxDate)})}
                            onChange={_changeFilters}
                        />
                    )}

                    <Spin spinning={loading}>
                        {error && <div className="sheet"><div className="empty">{error}</div></div>}
                        {current && <TabComponent tab={tab} data={current}/>}
                        {!current && !error && <div className="sheet"><div className="empty">Đang tải dữ liệu…</div></div>}
                    </Spin>

                    <div className="foot">
                        Số liệu tổng hợp từ lỗi QC ghi nhận và sản lượng theo từng công đoạn, tính lại lúc 05:00 và 17:00 mỗi ngày.
                        Ba ngày gần nhất chưa chốt sổ nên còn có thể thay đổi (chấm nhạt trên biểu đồ theo ngày).
                    </div>
                </div>
            </div>
        </TooltipProvider>
    )
}

export default NgRatePage
